/**
 * Tutorial Storage Utility
 * Manages tutorial preferences, state, and feature usage tracking
 * Syncs with backend API for cross-device persistence
 */

import tutorialService from '../services/tutorial.service';

const STORAGE_KEYS = {
  TUTORIAL_PREFERENCES: 'tutorial_preferences',
  TUTORIAL_SESSION: 'tutorial_session',
  FEATURE_USAGE: 'feature_usage',
  TUTORIAL_WATCHED: 'tutorial_watched',
  LAST_SYNC: 'tutorial_last_sync',
  PROMPT_SHOWN: 'tutorial_prompt_shown'
};

// Cache for backend data
let backendCache = {
  preferences: {},
  usageCounts: {},
  isSynced: false
};

/**
 * Sync tutorial data from backend
 * Call this on app initialization
 */
export const syncTutorialDataFromBackend = async () => {
  try {
    const response = await tutorialService.getTutorialData();
    if (response.success && response.data) {
      backendCache.preferences = response.data.preferences || {};
      backendCache.usageCounts = response.data.usageCounts || {};
      backendCache.isSynced = true;
      
      // Update localStorage cache
      localStorage.setItem(STORAGE_KEYS.TUTORIAL_PREFERENCES, JSON.stringify(backendCache.preferences));
      localStorage.setItem(STORAGE_KEYS.FEATURE_USAGE, JSON.stringify(backendCache.usageCounts));
      localStorage.setItem(STORAGE_KEYS.LAST_SYNC, Date.now().toString());
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error syncing tutorial data from backend:', error);
    // Fallback to localStorage if backend fails
    return false;
  }
};

/**
 * Get all tutorial preferences
 * Uses backend cache if synced, otherwise falls back to localStorage
 */
export const getTutorialPreferences = () => {
  try {
    if (backendCache.isSynced) {
      return { ...backendCache.preferences };
    }
    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEYS.TUTORIAL_PREFERENCES);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading tutorial preferences:', error);
    return {};
  }
};

/**
 * Set tutorial preference for a feature
 * @param {string} featureId - The feature identifier
 * @param {string} preference - 'dont_show' | 'remind_later' | 'watched'
 */
export const setTutorialPreference = async (featureId, preference) => {
  try {
    // Update local cache immediately
    backendCache.preferences[featureId] = preference;
    localStorage.setItem(STORAGE_KEYS.TUTORIAL_PREFERENCES, JSON.stringify(backendCache.preferences));
    
    // Sync with backend (fire and forget)
    tutorialService.setFeaturePreference(featureId, preference).catch(error => {
      console.error('Error syncing preference to backend:', error);
    });
  } catch (error) {
    console.error('Error setting tutorial preference:', error);
  }
};

/**
 * Get tutorial preference for a specific feature
 */
export const getTutorialPreference = async (featureId) => {
  // If synced, use cache
  if (backendCache.isSynced) {
    return backendCache.preferences[featureId] || null;
  }
  
  // Otherwise try to get from backend
  try {
    const preference = await tutorialService.getFeaturePreference(featureId);
    if (preference !== null) {
      backendCache.preferences[featureId] = preference;
    }
    return preference;
  } catch (error) {
    // Fallback to localStorage
    const preferences = getTutorialPreferences();
    return preferences[featureId] || null;
  }
};

/**
 * Check if tutorial should be shown for a feature
 * @param {string} featureId - The feature identifier
 * @returns {Promise<boolean>} True if tutorial should be shown
 */
export const shouldShowTutorial = async (featureId) => {
  // Check session state first (client-side only)
  const sessionState = getTutorialSessionState();
  if (sessionState.deferredFeatures?.includes(featureId)) {
    return false;
  }
  
  // Check backend if synced, otherwise use local storage
  if (backendCache.isSynced) {
    const preference = backendCache.preferences[featureId];
    const usageCount = backendCache.usageCounts[featureId] || 0;
    
    if (preference === 'dont_show' || preference === 'watched') {
      return false;
    }
    if (usageCount >= 3) {
      return false;
    }
    return true;
  }
  
  // Fallback: check backend API
  try {
    return await tutorialService.shouldShowTutorial(featureId);
  } catch (error) {
    console.error('Error checking if tutorial should be shown:', error);
    // Fallback to localStorage
    const preference = getTutorialPreferences()[featureId];
    const usageCount = getFeatureUsageCount(featureId);
    
    if (preference === 'dont_show' || preference === 'watched') {
      return false;
    }
    if (usageCount >= 3) {
      return false;
    }
    return true;
  }
};

/**
 * Get session state (tracks deferred features for current session)
 */
export const getTutorialSessionState = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.TUTORIAL_SESSION);
    const defaultState = {
      deferredFeatures: [],
      firstFeatureShown: false,
      promptShownFeatures: []
    };
    if (!stored) {
      return defaultState;
    }
    const parsed = JSON.parse(stored);
    return {
      ...defaultState,
      ...parsed,
      deferredFeatures: parsed?.deferredFeatures || [],
      promptShownFeatures: parsed?.promptShownFeatures || []
    };
  } catch (error) {
    console.error('Error reading tutorial session state:', error);
    return { deferredFeatures: [], firstFeatureShown: false, promptShownFeatures: [] };
  }
};

/**
 * Set session state
 */
export const setTutorialSessionState = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.TUTORIAL_SESSION, JSON.stringify(state));
  } catch (error) {
    console.error('Error setting tutorial session state:', error);
  }
};

/**
 * Mark feature as deferred for this session
 */
export const deferTutorialForSession = (featureId) => {
  const sessionState = getTutorialSessionState();
  if (!sessionState.deferredFeatures) {
    sessionState.deferredFeatures = [];
  }
  if (!sessionState.deferredFeatures.includes(featureId)) {
    sessionState.deferredFeatures.push(featureId);
  }
  setTutorialSessionState(sessionState);
};

/**
 * Check if this is the first feature tutorial in this session
 */
export const isFirstTutorialInSession = () => {
  const sessionState = getTutorialSessionState();
  return !sessionState.firstFeatureShown;
};

/**
 * Mark that first tutorial has been shown
 */
export const markFirstTutorialShown = () => {
  const sessionState = getTutorialSessionState();
  sessionState.firstFeatureShown = true;
  setTutorialSessionState(sessionState);
};

/**
 * Get feature usage count
 */
export const getFeatureUsageCount = (featureId) => {
  try {
    if (backendCache.isSynced) {
      return backendCache.usageCounts[featureId] || 0;
    }
    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEYS.FEATURE_USAGE);
    const usage = stored ? JSON.parse(stored) : {};
    return usage[featureId] || 0;
  } catch (error) {
    console.error('Error reading feature usage:', error);
    return 0;
  }
};

/**
 * Increment feature usage count
 */
export const incrementFeatureUsage = async (featureId) => {
  try {
    // Update local cache immediately
    const currentCount = backendCache.usageCounts[featureId] || 0;
    backendCache.usageCounts[featureId] = currentCount + 1;
    localStorage.setItem(STORAGE_KEYS.FEATURE_USAGE, JSON.stringify(backendCache.usageCounts));
    
    // Sync with backend (fire and forget)
    tutorialService.incrementFeatureUsage(featureId).catch(error => {
      console.error('Error syncing usage to backend:', error);
    });
    
    return backendCache.usageCounts[featureId];
  } catch (error) {
    console.error('Error incrementing feature usage:', error);
    return 0;
  }
};

/**
 * Mark tutorial as watched for a feature
 */
export const markTutorialWatched = async (featureId) => {
  await setTutorialPreference(featureId, 'watched');
};

/**
 * Clear session state (useful for testing or logout)
 * This clears session-specific data but keeps preferences and usage counts
 */
export const clearTutorialSession = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.TUTORIAL_SESSION);
  } catch (error) {
    console.error('Error clearing tutorial session:', error);
  }
};

/**
 * Check if feature prompt was shown in this session
 */
export const hasFeaturePromptShown = (featureId) => {
  const sessionState = getTutorialSessionState();
  if (sessionState.promptShownFeatures?.includes(featureId)) {
    return true;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PROMPT_SHOWN);
    if (!stored) {
      return false;
    }
    const parsed = JSON.parse(stored);
    return Boolean(parsed?.[featureId]);
  } catch (error) {
    console.error('Error reading prompt shown state:', error);
    return false;
  }
};

/**
 * Mark feature prompt as shown in this session
 */
export const markFeaturePromptShown = (featureId) => {
  const sessionState = getTutorialSessionState();
  if (!sessionState.promptShownFeatures) {
    sessionState.promptShownFeatures = [];
  }
  if (!sessionState.promptShownFeatures.includes(featureId)) {
    sessionState.promptShownFeatures.push(featureId);
    setTutorialSessionState(sessionState);
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PROMPT_SHOWN);
    const promptShown = stored ? JSON.parse(stored) : {};
    if (!promptShown[featureId]) {
      promptShown[featureId] = true;
      localStorage.setItem(STORAGE_KEYS.PROMPT_SHOWN, JSON.stringify(promptShown));
    }
  } catch (error) {
    console.error('Error saving prompt shown state:', error);
  }
};

/**
 * Clear all tutorial data (for logout)
 * This clears:
 * - Session state (sessionStorage)
 * - Backend cache (in-memory)
 * - LocalStorage cache (will be repopulated on next login from backend)
 * 
 * Note: Backend database data is NOT deleted - it persists for the user
 */
export const clearAllTutorialData = () => {
  try {
    // Clear session state
    sessionStorage.removeItem(STORAGE_KEYS.TUTORIAL_SESSION);
    
    // Clear backend cache
    backendCache = {
      preferences: {},
      usageCounts: {},
      isSynced: false
    };
    
    // Clear localStorage cache
    // Note: localStorage.clear() in signout.js will handle this,
    // but we clear our specific keys here for safety
    localStorage.removeItem(STORAGE_KEYS.TUTORIAL_PREFERENCES);
    localStorage.removeItem(STORAGE_KEYS.FEATURE_USAGE);
    localStorage.removeItem(STORAGE_KEYS.TUTORIAL_WATCHED);
    localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
    localStorage.removeItem(STORAGE_KEYS.PROMPT_SHOWN);
    
    console.log('Tutorial data cleared on logout');
  } catch (error) {
    console.error('Error clearing all tutorial data:', error);
  }
};

